import { httpClient, publicHttpClient } from '@/lib/httpClient';
import { API_CONFIG } from '@/config/api';
import type { Job } from '@/types';

/**
 * Provider Job Service
 * 
 * Maps to backend APIs:
 * - GET /api/v1/jobs - List all available jobs (public)
 * - GET /api/v1/jobs/search - Search jobs with filters
 * - GET /api/v1/jobs/{id} - Get job details
 */
export class ProviderJobService {
  private getAuthToken(): string | undefined {
    return localStorage.getItem('auth_token') || undefined;
  }

  /**
   * Get all available jobs using GET /api/v1/jobs
   * For providers to browse and apply to
   */
  async getAvailableJobs(filters?: {
    category?: string;
    location?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{ jobs: Job[]; total: number; page: number; totalPages: number }> {
    const params: Record<string, string> = {};
    
    if (filters?.category) params.category = filters.category;
    if (filters?.location) params.location = filters.location;
    if (filters?.search) params.search = filters.search;
    if (filters?.page) params.page = filters.page.toString();
    if (filters?.limit) params.limit = filters.limit.toString();

    const response = await httpClient.get(API_CONFIG.ENDPOINTS.JOBS, {
      params,
      // Jobs endpoint is public, but include token for better data
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch available jobs');
    }

    const jobs = (response.data as any)?.jobs || (response.data as any) || [];
    const availableJobs = jobs.map((job: any) => this.mapJobFromBackend(job));

    return {
      jobs: availableJobs.filter((job: Job) => job.status === 'OPEN'),
      total: (response.data as any)?.total || availableJobs.length,
      page: (response.data as any)?.page || 1,
      totalPages: (response.data as any)?.totalPages || 1
    };
  }

  /**
   * Get active jobs (quote-accepted, assigned, in-progress) using GET /api/v1/auth/providers/jobs/active
   * For providers to see jobs where their quote was accepted
   */
  async getActiveJobs(filters?: {
    status?: string;
    limit?: number;
  }): Promise<{ jobs: Job[]; total: number }> {
    const params: Record<string, string> = {};
    
    if (filters?.status) params.status = filters.status;
    if (filters?.limit) params.limit = filters.limit.toString();

    const response = await httpClient.get('/api/v1/auth/providers/jobs/active', {
      params,
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch active jobs');
    }

    const jobs = (response.data as any)?.data || (response.data as any) || [];
    const activeJobs = jobs.map((job: any) => this.mapJobFromBackend(job));

    return {
      jobs: activeJobs,
      total: activeJobs.length
    };
  }

  /**
   * Search jobs using GET /api/v1/jobs/search
   */
  async searchJobs(query: string, filters?: {
    category?: string;
    location?: string;
    budget_min?: number;
    budget_max?: number;
    priority?: string;
  }): Promise<Job[]> {
    const params: Record<string, string> = {
      q: query
    };
    
    if (filters?.category) params.category = filters.category;
    if (filters?.location) params.location = filters.location;
    if (filters?.budget_min) params.budget_min = filters.budget_min.toString();
    if (filters?.budget_max) params.budget_max = filters.budget_max.toString();
    if (filters?.priority) params.priority = filters.priority;

    const response = await httpClient.get(API_CONFIG.ENDPOINTS.JOB_SEARCH, {
      params,
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to search jobs');
    }

    const jobs = ((response.data as any)?.jobs || (response.data as any) || []).map((job: any) => this.mapJobFromBackend(job));
    
    // Filter to only show OPEN jobs for providers
    return jobs.filter((job: Job) => job.status === 'OPEN');
  }

  /**
   * Get job details using GET /api/v1/jobs/{id}
   */
  async getJobById(jobId: string): Promise<Job | null> {
    const response = await httpClient.get<Job>(`${API_CONFIG.ENDPOINTS.JOBS}/${jobId}`, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      if (response.error?.includes('not found')) return null;
      throw new Error(response.error || 'Failed to fetch job');
    }

    return this.mapJobFromBackend(response.data);
  }

  /**
   * Get job categories using GET /api/v1/jobs/categories
   */
  async getJobCategories(): Promise<any[]> {
    const response = await publicHttpClient.get(API_CONFIG.ENDPOINTS.JOB_CATEGORIES, {
      auth: false // 🎯 EXPLICIT: This is a public endpoint
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch job categories');
    }

    return (response.data as any)?.data || (response.data as any) || [];
  }

  /**
   * Map backend job data to frontend Job type
   */
  private mapJobFromBackend(data: any): Job {
    const mapPriority = (priority: string): 'LOW' | 'MEDIUM' | 'HIGH' => {
      switch (priority?.toLowerCase()) {
        case 'low': return 'LOW';
        case 'medium': return 'MEDIUM';
        case 'high': return 'HIGH';
        default: return 'MEDIUM';
      }
    };

    const mapStatus = (status: string): Job['status'] => {
      switch (status?.toLowerCase()) {
        case 'open': return 'OPEN';
        case 'assigned': return 'IN_PROGRESS';
        case 'in_progress': return 'IN_PROGRESS';
        case 'completed': return 'COMPLETED';
        case 'cancelled': return 'CANCELLED';
        default: return 'OPEN';
      }
    };

    return {
      id: data.id,
      title: data.title,
      description: data.description,
      category: data.category_name || data.category || 'General',
      location: data.location,
      urgency: mapPriority(data.priority),
      status: mapStatus(data.status),
      customerId: data.customer_id || data.customerId,
      customerName: data.customer_name || data.customerName,
      assignedProviderId: data.provider_id || data.providerId,
      budgetRange: data.budget_min || data.budget_max ? {
        min: Number(data.budget_min) || 0,
        max: Number(data.budget_max) || Number(data.budget_min) || 0
      } : undefined,
      images: data.images || [],
      preferredDate: data.scheduled_date ? new Date(data.scheduled_date) : undefined,
      quoteCount: Number(data.quote_count) || 0,
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at),
      completedDate: data.completed_at ? new Date(data.completed_at) : undefined
    };
  }
}

export const providerJobService = new ProviderJobService();